import React, { useEffect } from "react";

import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";

import { usePageTitle } from "@/atoms/page-title-atom";
import { gql } from "@apollo/client";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { MetricHeader } from "@components/metric-header";
import { PathElementList } from "@components/path-element";
import {
  CompanionWrapper,
  ScreenWithControlCenter,
} from "@components/reports/control-center-utilities";
import { LoadingSlide, NoDataView } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import styled from "@emotion/styled";
import { useNorthbeamQuery } from "@hooks/http";
import { logEvent } from "@internal/analytics";
import {
  CustomerPathReportSorting,
  SortDirection,
  type GetCustomerPathReport,
  type GetCustomerPathReportVariables,
} from "@north-beam/nb-client-side";
import {
  formatting,
  jsonHash,
  metrics,
  todayLocalAsISODate,
} from "@north-beam/nb-common";
import { makeReportStateQuery } from "@pages/objects/report-state";
import { MetricValue } from "@shared/metric-value";
import { parseDateRangeArgument } from "@utils/index";

import { ControlCenter } from "./control-center";
import {
  makeReportStateParams,
  parseReportState,
  type ReportState,
} from "./report-state";

const GET_CONVERSION_PATH_REPORT = gql`
  query GetCustomerPathReport(
    $dateRange: JSONObject!
    $pathDateRangeFilterType: PathDateRangeFilterType!
    $pathLengthMinimum: Int
    $pathLengthMaximum: Int
    $showConvertingPathsOnly: Boolean!
    $hasExclusions: Boolean
    $sortBy: CustomerPathReportSorting!
    $sortByDirection: SortDirection!
    $pathFilterStages: [CustomerPathFilterStage!]!
    $limit: Int!
  ) {
    me {
      id
      customerPathReport(
        dateRange: $dateRange
        pathDateRangeFilterType: $pathDateRangeFilterType
        pathLengthMinimum: $pathLengthMinimum
        pathLengthMaximum: $pathLengthMaximum
        limit: $limit
        sortBy: $sortBy
        sortByDirection: $sortByDirection
        showConvertingPathsOnly: $showConvertingPathsOnly
        hasExclusions: $hasExclusions
        pathFilterStages: $pathFilterStages
      ) {
        totalCount
        totalTransactions
        totalRevenue
        totalShopifyOrdersInTimePeriod
        numberShopifyOrdersCapturedInTimePeriod
        rows {
          count
          transactions
          revenue
          pathElements {
            elementId
            name
            objectId
            nbtPlatformID
            count
          }
        }
      }
    }
  }
`;

export const PathsPage = () => {
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Customer Paths");
  }, [setPageTitle]);

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const [isLoading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const state = parseReportState(search);

  const { dateRange: dr, ...rest } = state;

  const anchor = todayLocalAsISODate();
  const dateRange = parseDateRangeArgument(dr, anchor);

  const variables: Omit<
    GetCustomerPathReportVariables,
    "sortBy" | "sortByDirection"
  > = {
    dateRange,
    ...rest,
    hasExclusions: true,
  };

  /* Tricky chain here:
     1. URL is updated with new slug
     2. URL slug determines report parameters in child components */
  const updateReport = React.useCallback(
    (newReportState: ReportState) =>
      navigate({
        search: makeReportStateParams(newReportState).toString(),
      }),
    [navigate],
  );

  return (
    <ScreenWithControlCenter>
      <ControlCenter
        breakdownConfigs={breakdownConfigs}
        initialState={state}
        onReportStateUpdated={updateReport}
        isLoading={isLoading}
      />
      <CompanionWrapper>
        <ReportDynamicArea variables={variables} setLoading={setLoading} />
      </CompanionWrapper>
    </ScreenWithControlCenter>
  );
};

interface FetchReportParams {
  variables: Omit<
    GetCustomerPathReportVariables,
    "sortBy" | "sortByDirection" | "showPaidPathsOnly"
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

function ReportDynamicArea({
  variables: baseVariables,
  setLoading,
}: FetchReportParams) {
  const { search } = useLocation();
  const { user } = useUser();
  const state = parseReportState(search);

  const [sortBy, setSortBy] = React.useState<CustomerPathReportSorting>(
    CustomerPathReportSorting.REVENUE,
  );
  const [sortByDirection, setSortByDirection] = React.useState<SortDirection>(
    SortDirection.DESCENDING,
  );

  const variables = {
    ...baseVariables,
    sortBy,
    sortByDirection,
  };

  const { data, loading } = useNorthbeamQuery<
    GetCustomerPathReport,
    GetCustomerPathReportVariables
  >(GET_CONVERSION_PATH_REPORT, { variables });

  const variablesHash = jsonHash(variables);

  React.useEffect(() => setLoading(loading), [loading, setLoading]);
  React.useEffect(() => {
    if (loading) {
      logEvent("Request Customer Path Report", JSON.parse(variablesHash));
    }
  }, [loading, variablesHash]);

  const [response, setResponse] = React.useState(data?.me?.customerPathReport);
  React.useEffect(() => {
    if (data?.me?.customerPathReport) {
      setResponse(data.me.customerPathReport);
    }
  }, [data]);

  if (response) {
    if (response.rows.length === 0) {
      return <NoDataView />;
    }

    const {
      rows,
      totalCount,
      /* totalEstimatedCost,
         totalCostPerAcquisition,
         totalReturnOnAdSpend, */
      totalRevenue,
      totalTransactions,
      totalShopifyOrdersInTimePeriod,
      numberShopifyOrdersCapturedInTimePeriod,
    } = response;

    const adObjectReportParams = makeReportStateQuery({
      attributionModel: user.getDefaultAttributionModel(undefined),
      dateRange: state.dateRange,
      nbAccountingMode: "accrual",
      attributionWindow: "1",
      compareDateRange: "lastPeriod",
      timeGranularity: "daily",
    }).toString();

    const caretDirection =
      sortByDirection === SortDirection.ASCENDING ? "up" : "down";
    const caretSort = (
      <i className={`fa-regular fa-sharp fa-arrow-${caretDirection}`} />
    );

    const columns = [
      {
        name: "Paths",
        totalValue: totalCount,
        format: metrics.MetricTypes.INTEGER,
        sortKey: CustomerPathReportSorting.COUNT,
        rowKey: "count",
      },
      /* {
           name: "Cost (est.)",
           totalValue: totalEstimatedCost,
           format: metrics.MetricTypes.CURRENCY,
           sortKey: CustomerPathReportSorting.ESTIMATED_COST,
           rowKey: "estimatedCost",
         },
         {
           name: "ROAS",
           totalValue: totalReturnOnAdSpend,
           format: metrics.MetricTypes.MULTIPLIER,
           sortKey: CustomerPathReportSorting.RETURN_ON_AD_SPEND,
           rowKey: "returnOnAdSpend",
         },
         {
           name: "CPA",
           totalValue: totalCostPerAcquisition,
           format: metrics.MetricTypes.CURRENCY,
           sortKey: CustomerPathReportSorting.COST_PER_ACQUISITION,
           rowKey: "costPerAcquisition",
         }, */
      {
        name: "Txns",
        totalValue: totalTransactions,
        format: metrics.MetricTypes.INTEGER,
        sortKey: CustomerPathReportSorting.TRANSACTIONS,
        rowKey: "transactions",
      },
      {
        name: "Rev",
        totalValue: totalRevenue,
        format: metrics.MetricTypes.CURRENCY,
        sortKey: CustomerPathReportSorting.REVENUE,
        rowKey: "revenue",
      },
    ] as const;

    let pctOrdersCaptured: string | null = null;
    if (
      numberShopifyOrdersCapturedInTimePeriod &&
      totalShopifyOrdersInTimePeriod
    ) {
      pctOrdersCaptured = formatting.formatMetric(
        numberShopifyOrdersCapturedInTimePeriod /
          totalShopifyOrdersInTimePeriod,
        metrics.MetricTypes.PERCENTAGE,
        { precision: "exact" },
      );
    }

    const headers = columns.map(({ name, totalValue, format, sortKey }) => (
      <div className="td nb-col-metric" key={sortKey}>
        <div
          className="nb-col-metric-header"
          onClick={() => {
            if (sortKey !== sortBy) {
              setSortBy(sortKey);
              setSortByDirection(SortDirection.DESCENDING);
            } else {
              setSortByDirection(
                sortByDirection === SortDirection.DESCENDING
                  ? SortDirection.ASCENDING
                  : SortDirection.DESCENDING,
              );
            }
          }}
        >
          <div className="d-inline-block flex-grow-1" />
          <div className="nb-sort">{sortBy === sortKey && caretSort}</div>
          <div className="header">
            <MetricHeader
              name={name}
              value={formatting.formatMetric.approximate(totalValue, format, {
                code: user.currencies.currentCurrency,
              })}
              align="right"
              height={1}
            />
            <div
              className="small text-muted"
              style={{
                height: "2em",
                lineHeight: "1.1em",
                marginTop: "0.5em",
              }}
            >
              {name === "Txns" && pctOrdersCaptured !== null ? (
                <GenericTooltip
                  placement="top"
                  content={
                    "Percentage of total orders (reported by Shopify) in the " +
                    "selected time period in this report."
                  }
                  mouseLeaveDelay={0}
                  mouseEnterDelay={0}
                  destroyTooltipOnHide={true}
                >
                  <span>
                    <i className="fa-regular fa-sharp fa-info-circle" />{" "}
                    {pctOrdersCaptured} of total
                  </span>
                </GenericTooltip>
              ) : (
                <React.Fragment>&nbsp;</React.Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    ));

    return (
      <div className="px-4">
        <Style className="bg-white p-4">
          <div className="thead">
            <div className="tr">
              <div className="td nb-col-rank" />
              <div className="td nb-col-item">
                <MetricHeader name="Path Elements" value="" height={1} />
              </div>
              {headers}
            </div>
          </div>
          <div
            className={clsx("tbody", (loading && "waiting-interstitial") || "")}
          >
            {rows.map((row, index) => {
              return (
                <div className="tr" key={jsonHash(row.pathElements)}>
                  <div className="td nb-col-rank">{index + 1}</div>
                  <div className="td nb-col-item">
                    <PathElementList
                      pathElements={row.pathElements}
                      adObjectReportParams={adObjectReportParams}
                    />
                  </div>
                  {columns.map(({ rowKey, format }) => (
                    <MetricValue
                      key={rowKey}
                      format={format}
                      precision="exact"
                      value={row[rowKey]}
                      className="td nb-col-metric"
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </Style>
      </div>
    );
  }

  if (loading) {
    return <LoadingSlide />;
  }

  return <NoDataView />;
}

const Style = styled.div`
  .thead {
    margin-bottom: 0.5rem;
  }

  .tr {
    display: flex;
    align-items: center;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
  }

  .tbody {
    font-size: 12px;
    .tr {
      border-top: 1px solid #aaaaaa;
    }
    .tr:last-child {
      border-bottom: 1px solid #aaaaaa;
    }
  }

  .td {
    padding-left: 0.75rem;
  }

  .nb-col-rank {
    flex: 0.25;
    text-align: right;
  }

  .nb-col-item {
    flex: 5;
  }

  .nb-col-metric {
    flex: 1.5;
    text-align: right;

    .nb-col-metric-header {
      cursor: pointer;
      display: flex;
      flex-direction: row;
      text-align: right;

      .header {
        text-align: right;
      }

      .nb-sort {
        margin-left: 0.5rem;
        margin-right: 0.25rem;
        max-width: 1em;
        width: 1em;
        font-size: 80%;
      }
    }
  }
`;
